import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DataService } from '@app/core/services/api';
import { keys, LocalStorageService } from '@app/core/services/storage/local-storage.service';
import { ToastComponent } from '@app/shared/components/toast/toast.component';
import { AppUtils } from '@app/shared/utils/app-utils';
import { select, Store } from '@ngrx/store';
import { State } from '@app/shared/store/reducers';
import * as _ from 'lodash';
import { firstValueFrom, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { getCurrUser } from '@app/shared/store/selectors/user-claims.selector';
import { abilities } from '@app/core/constant/access';

interface CastProfileCheck {
  firstName: string;
  lastName: string;
  middleName: string;
  knownAs: string;
  photoUrl: string;
  perner: string;
  orgUnitName: string;
  employeeGroupName: string;
  employeeSubGroupName: string;
  site: string;
  siteCode: string;
  role: string;
  gender: string;
  statusName?: string;
}

@Component({
  standalone: false,
  selector: 'app-create-cast-member-modal',
  templateUrl: './create-cast-member-modal.component.html',
  styleUrls: ['./create-cast-member-modal.component.scss']
})
export class CreateCastMemberModalComponent implements OnInit, OnDestroy {
  @ViewChild('createCastMemberModal') modal: ElementRef;
  @ViewChild(ToastComponent) wdprtoast: ToastComponent;
  @ViewChild(FormGroupDirective) formGroupDirective: FormGroupDirective;

  form: FormGroup = this.formBuilder.group({
    pernr: ['', Validators.required],
    site: [''],
    siteCode: ['', Validators.required],
    role: ['', Validators.required],
    gender: ['', Validators.required]
  });
  siteOptions: any[] = [];
  roleOptions: any[] = [];
  genderOptions: any[] = [
    { text: 'Select Gender', value: '' },
    { text: 'Male', value: 'M' },
    { text: 'Female', value: 'F' }
  ];
  cast: CastProfileCheck = null;
  pernr$: Subscription;
  searched = false;
  submitted = false;
  isLoading = false;
  errCode: number = null;
  formChanges$: Subscription;
  userState$: Subscription;
  configuredSites: string[] = [];

  constructor(
    private store: Store<State>,
    private appUtils: AppUtils,
    private formBuilder: FormBuilder,
    private dataService: DataService,
    private router: Router,
    private localService: LocalStorageService
  ) { }

  ngOnInit(): void {
    this.userState$ = this.store.pipe(select(getCurrUser)).subscribe((res: any) => {
      if (res) {
        const userAbilities = res.user.decodedToken.functionalAbilities;
        this.configuredSites = this.appUtils.getConfiguredSites(abilities.EDIT_CAST_MEMBERS, userAbilities);
      }
    });

    this.formChanges$ = this.pernr.valueChanges.pipe(
      debounceTime(100), distinctUntilChanged()).subscribe(pernr => {
        if (pernr === '') {
          this.resetForm();
        }
      }
      );
  }

  async onSearch() {
    try {
      this.searched = true;
      this.errCode = null;
      this.isLoading = true;
      const pernr = this.pernr.value;
      const response: CastProfileCheck = await firstValueFrom(this.dataService.checkCastProfile(pernr));
      this.cast = response;
      this.resetSelections(false);
      this.isLoading = false;
    } catch (error) {
      console.log(error);

      this.cast = null;
      this.resetSelections(true);
      this.isLoading = false;

      if (error.status) {
        this.errCode = error.status;
      } else {
        this.errCode = null;
      }
    }
  }

  onSubmit() {
    this.submitted = true;
    this.cast.site = _.find(this.siteOptions, { value: this.siteCode.value }).text;
    this.cast.siteCode = this.siteCode.value;
    this.cast.role = this.role.value;
    this.cast.gender = this.gender.value;
    this.router.navigate(['/cast-measurement'], { queryParams: this.cast });
    this.close();
  }

  getSites() {
    this.siteOptions = [];
    const userSites = this.configuredSites;
    const sites = this.localService.getData(keys.sites).filter(item => userSites.includes(item.site_code));
    if (sites && sites.length > 0) {
      sites.forEach((site: any) => {
        this.siteOptions.push({
          value: site.site_code,
          text: site.site_name
        });
      });
    }
  }

  getRoles() {
    if (this.roleOptions.length === 0) {
      const roles = this.localService.getData(keys.roles);
      if (roles && roles.length > 0) {
        roles.forEach((role: any) => {
          this.roleOptions.push({
            value: role.id,
            text: role.name
          });
        });
      }
    }
  }

  resetForm() {
    this.submitted = false;
    this.searched = false;
    this.pernr.setValue('');
    this.siteCode.setValue('');
    this.role.setValue('');
    this.gender.setValue('');
  }

  resetSelections(clearValidations: boolean) {
    this.siteCode.setValue('');
    this.role.setValue('');
    this.gender.setValue('');

    if (clearValidations) {
      this.form.updateValueAndValidity();
      this.form.markAsUntouched();
      this.form.markAsPristine();
    }
  }

  open() {
    this.getSites();
    this.getRoles();
    this.resetForm();
    this.resetSelections(true);
    this.modal.nativeElement.open = true;
    this.modal.nativeElement.shadowRoot.querySelector('.header').removeAttribute('hidden');
    this.modal.nativeElement.shadowRoot.querySelector('#buttonsSeparator').removeAttribute('hidden');
    this.modal.nativeElement.shadowRoot.querySelector('#buttonsWrapper').removeAttribute('hidden');
  }

  close() {
    this.resetForm();
    this.resetSelections(true);
    this.modal.nativeElement.open = false;
  }

  get pernr() {
    return this.form.get('pernr');
  }

  get site() {
    return this.form.get('site');
  }

  get siteCode() {
    return this.form.get('siteCode');
  }

  get role() {
    return this.form.get('role');
  }

  get gender() {
    return this.form.get('gender');
  }

  get isContinueBtnDisabled(): boolean {
    return this.form.valid && this.cast !== null ? false : true;
  }

  ngOnDestroy(): void {
    this.userState$.unsubscribe();
    if (this.formChanges$) {
      this.formChanges$.unsubscribe();
    }
  }
}
